import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Users, RefreshCw, Eye, Calendar, Mail, AtSign, ShieldCheck, User, BadgeCheck, Globe, Heart } from "lucide-react";
import UserAvatar from "../../components/UserAvatar";
import {
  API,
  getAdminSessionToken,
  useToast,
  ConfirmModal,
  Modal,
  PageHeader,
  TableCard,
  TableHead,
  TableSkeleton,
  DeleteBtn,
  SearchBar,
  EmptyState,
  Pagination,
} from "./_shared";
import { useAuth } from "../../contexts/AuthContext";

const ADMIN_ROLE_LABEL = { owner: "Chủ sở hữu", reviewer: "Biên tập duyệt", editor: "Biên tập viên", viewer: "Chỉ xem" };
// Badge nền đặc, chữ trắng — đậm như các nút hành động
const ADMIN_ROLE_TONE = {
  owner: "bg-violet-600",
  reviewer: "bg-sky-600",
  editor: "bg-amber-500",
  viewer: "bg-slate-600",
};
const PILL = "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-white shadow-sm";

const RoleBadge = ({ role, adminRole }) => role === "admin" ? (
  <span className={`${PILL} ${ADMIN_ROLE_TONE[adminRole || "owner"]}`}>
    <ShieldCheck size={12} />
    {ADMIN_ROLE_LABEL[adminRole || "owner"]}
  </span>
) : (
  <span className={`${PILL} bg-emerald-600`}>
    <User size={12} />
    User
  </span>
);

const VerifiedBadge = ({ verified }) => (
  <span className={`${PILL} ${verified ? "bg-emerald-600" : "bg-slate-500"}`}>
    <BadgeCheck size={12} /> {verified ? "Verified" : "Unverified"}
  </span>
);

const ProviderBadge = ({ provider }) => (
  <span className={`${PILL} ${provider === "google" ? "bg-blue-600" : "bg-slate-700"}`}>
    <Globe size={12} /> {provider || "local"}
  </span>
);

const ViewBtn = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="Xem"
    aria-label="Xem"
    className="flex h-9 w-9 items-center justify-center rounded-lg bg-admin-primary text-white shadow-sm transition hover:bg-admin-primaryHover active:scale-[0.97]"
  >
    <Eye size={15} />
  </button>
);

/** Một dòng thông tin chỉ đọc trong modal xem. */
const InfoRow = ({ label, children, mono }) => (
  <div className="rounded-2xl border border-admin-border bg-admin-muted/40 px-4 py-3">
    <p className="text-[11px] font-bold uppercase tracking-wider text-admin-textMuted">{label}</p>
    <div className={`mt-1 break-all text-sm font-semibold text-admin-text ${mono ? "font-mono text-xs" : ""}`}>
      {children || <span className="text-admin-textMuted">—</span>}
    </div>
  </div>
);

const PAGE_SIZES = [20, 50, 100];

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("vi-VN") : "");

const AdminUsers = () => {
  const { token: authUserToken } = useAuth();
  const { push: toast, ToastPortal } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [viewUser, setViewUser] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [noAuth, setNoAuth] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  /** Token phiên đang đăng nhập (context) trước, rồi localStorage — không ưu tiên adminToken cũ trước authToken. */
  const getToken = useCallback(
    () => authUserToken || getAdminSessionToken(),
    [authUserToken]
  );

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setNoAuth(false);
    try {
      // API phân trang (mặc định 20/trang) — lấy lần lượt các trang để danh sách, tìm kiếm và đếm không bị cụt
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
      const all = [];
      let page = 1;
      let pages = 1;
      do {
        const res = await window.fetch(`${API}/users?page=${page}&limit=200`, { headers });
        if (res.status === 401 || res.status === 403) { setNoAuth(true); return; }
        const data = await res.json();
        if (Array.isArray(data)) { all.push(...data); break; }
        all.push(...(data?.users || data?.data || []));
        pages = data?.pagination?.pages || 1;
        page += 1;
      } while (page <= pages && page <= 25);
      setUsers(all);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [getToken, toast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDelete = async (id) => {
    setConfirm(null);
    try {
      const res = await window.fetch(`${API}/users/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Lỗi từ server");
      toast("Đã xoá tài khoản!", "success");
      loadUsers();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const favorites = useMemo(() => {
    const favs = viewUser?.favorites;
    if (!Array.isArray(favs)) return { count: 0, names: [] };
    return { count: favs.length, names: favs.map((f) => f?.name).filter(Boolean) };
  }, [viewUser]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchQ = !q || u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
    return matchQ && (!filterRole || u.role === filterRole);
  });

  // Phân trang phía client (đã tải đủ danh sách); đổi tìm kiếm / bộ lọc / số dòng → về trang 1
  useEffect(() => { setPage(1); }, [search, filterRole, pageSize]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, pages);
  const pageRows = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const adminCount = users.filter((u) => u.role === "admin").length;
  const userCount = users.filter((u) => u.role === "user").length;

  if (noAuth) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-amber-100 bg-amber-50 text-amber-600">
        <ShieldCheck size={28} strokeWidth={1.5} />
        <p className="font-semibold">Không có quyền truy cập</p>
        <p className="text-sm text-amber-500">Vai trò của bạn chưa được cấp quyền quản lý tài khoản. Hãy liên hệ chủ sở hữu hệ thống.</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <PageHeader
        title="Quản lý người dùng"
        titleClassName="text-3xl font-extrabold tracking-tight text-admin-text md:text-4xl"
        subtitle={`${users.length} tài khoản · ${adminCount} admin · ${userCount} user`}
        action={
          <>
            <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm người dùng..." />
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
              className="rounded-xl border border-admin-border bg-admin-surface px-3 py-2.5 text-sm text-admin-text outline-none shadow-sm transition focus:border-admin-primary focus:ring-2 focus:ring-[rgba(37,106,191,0.14)]">
              <option value="">Tất cả role</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button onClick={loadUsers} title="Tải lại"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:border-admin-primary/25 hover:bg-admin-primaryLight hover:text-admin-primary active:scale-95">
              <RefreshCw size={15} />
            </button>
          </>
        }
      />

      <TableCard>
        {loading ? (
          <TableSkeleton cols={5} rows={7} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={Users} title="Không có người dùng nào"
            description={search ? "Thử tìm kiếm với từ khoá khác" : "Chưa có tài khoản nào"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <TableHead cols={["Người dùng", "Liên hệ", "Role", "Ngày tham gia", ""]} />
              <tbody className="divide-y divide-admin-border">
                {pageRows.map((u) => (
                  <tr key={u._id} className="transition-colors hover:bg-admin-muted/60">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <UserAvatar url={u.avatar} name={u.fullName} sizeClass="h-9 w-9" textClass="text-xs" roundedClass="rounded-full" />
                        <div>
                          <p className="font-semibold text-admin-text">{u.fullName}</p>
                          <p className="text-[11px] text-admin-textMuted">#{u._id?.slice(-6)}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-3.5">
                      {u.email && (
                        <div className="flex items-center gap-1.5 text-xs text-admin-textMuted">
                          <Mail size={11} className="shrink-0 text-admin-textMuted" />{u.email}
                        </div>
                      )}
                      {u.username && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-admin-textMuted">
                          <AtSign size={11} className="shrink-0 text-admin-textMuted" />{u.username}
                        </div>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <VerifiedBadge verified={u.emailVerified} />
                        <ProviderBadge provider={u.authProvider} />
                      </div>
                    </td>

                    <td className="px-5 py-3.5"><RoleBadge role={u.role} adminRole={u.adminRole} /></td>

                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-xs text-admin-textMuted">
                        <Calendar size={11} />
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString("vi-VN") : "—"}
                      </div>
                    </td>

                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <ViewBtn onClick={() => setViewUser(u)} />
                        <DeleteBtn variant="solid" onClick={() => setConfirm(u._id)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-admin-border">
            <Pagination page={curPage} pages={pages} total={filtered.length} onChange={setPage} />
            <label className="flex items-center gap-2 px-5 py-2 text-xs text-admin-textMuted">
              Hiện
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Số dòng mỗi trang"
                className="h-8 rounded-lg border border-admin-border bg-admin-surface px-2 text-xs text-admin-text outline-none focus:border-admin-primary"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              dòng
            </label>
          </div>
        ) : null}
      </TableCard>

      {/* Modal xem chi tiết (chỉ đọc) */}
      <Modal
        open={Boolean(viewUser)}
        onClose={() => setViewUser(null)}
        title="Thông tin tài khoản"
        maxWidthClass="max-w-3xl"
      >
        {viewUser && (
          <div className="space-y-5">
            <div className="flex items-center gap-4 rounded-3xl border border-admin-border bg-admin-muted/60 p-4">
              <UserAvatar url={viewUser.avatar} name={viewUser.fullName} sizeClass="h-16 w-16" textClass="text-base" roundedClass="rounded-full" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-bold text-admin-text">{viewUser.fullName || "—"}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <RoleBadge role={viewUser.role} adminRole={viewUser.adminRole} />
                  <VerifiedBadge verified={viewUser.emailVerified} />
                  <ProviderBadge provider={viewUser.authProvider} />
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <InfoRow label="Họ tên">{viewUser.fullName}</InfoRow>
              <InfoRow label="Email">{viewUser.email}</InfoRow>
              <InfoRow label="Username">{viewUser.username}</InfoRow>
              <InfoRow label="Role">{viewUser.role === "admin" ? `Admin — ${ADMIN_ROLE_LABEL[viewUser.adminRole || "owner"]}` : "User"}</InfoRow>
              <InfoRow label="Ngày tham gia">{fmtDateTime(viewUser.createdAt)}</InfoRow>
              <InfoRow label="Cập nhật lần cuối">{fmtDateTime(viewUser.updatedAt)}</InfoRow>
              <InfoRow label="User ID" mono>{viewUser._id}</InfoRow>
            </div>

            <div className="rounded-3xl border border-admin-border bg-admin-surface p-5">
              <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted">
                <Heart size={12} /> Yêu thích · {favorites.count} địa điểm
              </p>
              {favorites.names.length > 0 ? (
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                  {favorites.names.map((n, i) => (
                    <li key={`${n}-${i}`} className="truncate rounded-2xl border border-admin-border bg-admin-muted/40 px-3 py-2 text-sm font-semibold text-admin-text">
                      {n}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-admin-textMuted">Chưa có yêu thích.</p>
              )}
            </div>

            <div className="flex justify-end border-t border-admin-border pt-4">
              <button type="button" onClick={() => setViewUser(null)}
                className="rounded-2xl border border-admin-border bg-admin-surface px-6 py-2.5 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted">
                Đóng
              </button>
            </div>
          </div>
        )}
      </Modal>

      {confirm && (
        <ConfirmModal
          message="Xoá tài khoản này?"
          description="Hành động này không thể hoàn tác và sẽ xoá toàn bộ dữ liệu liên quan."
          onConfirm={() => handleDelete(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}

      <ToastPortal />
    </div>
  );
};

export default AdminUsers;
